/**
 * Which Firebase project the widget talks to.
 *
 * Deliberately free of any `firebase/*` import: the host page can hand over a
 * config (via `initFirebase()` or the widget's `firebaseConfig` prop) without
 * pulling the Firebase SDK into its bundle. The SDK is only loaded when the
 * widget is actually used — see `MarkupWidget`.
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * The studio's shared project, used when the host supplies no config of its own.
 * Firebase web config is public by design; access is controlled by the
 * security rules in /firestore.rules and /storage.rules.
 */
export const DEFAULT_FIREBASE_CONFIG: FirebaseConfig = {
  apiKey: 'AIzaSyD2rQ-HOEU4VW5h5CGmKQK9FcBDp2bi98g',
  authDomain: 'markup-db905.firebaseapp.com',
  projectId: 'markup-db905',
  storageBucket: 'markup-db905.firebasestorage.app',
  messagingSenderId: '330137146006',
  appId: '1:330137146006:web:264638f50408c3864428b7',
};

const REQUIRED: (keyof FirebaseConfig)[] = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
];

let configured: FirebaseConfig | null = null;

/**
 * Point the widget at a Firebase project. Call once at the app root, or pass
 * `firebaseConfig` to the widget. An incomplete config is rejected with a
 * warning rather than half-initialising Firebase.
 */
export function initFirebase(config: FirebaseConfig): boolean {
  const missing = REQUIRED.filter((key) => !config?.[key]);
  if (missing.length > 0) {
    console.warn(
      `[markup] Firebase config is missing ${missing.join(', ')}; using the default project.`,
    );
    return false;
  }
  configured = config;
  return true;
}

export function getFirebaseConfig(): FirebaseConfig {
  return configured ?? DEFAULT_FIREBASE_CONFIG;
}

/**
 * Local Firebase emulators, for development and the end-to-end tests. Never
 * set in production — with it, nothing reaches the real project.
 */
export interface FirebaseEmulators {
  host?: string;
  auth?: number;
  firestore?: number;
  storage?: number;
}

let emulators: FirebaseEmulators | null = null;

export function setFirebaseEmulators(settings: FirebaseEmulators | null): void {
  emulators = settings;
}

export function getFirebaseEmulators(): FirebaseEmulators | null {
  return emulators;
}
